import { APIError, defaultParseError } from "@certn/api";
import { t } from "i18next";
import { array, number, object, string, type NumberSchema } from "yup";

import { customInstructionsSchemaField } from "../templates/customInstructionsValidation";

export const MAX_REFERENCE_QUANTITY = 5;

export type ReferenceCheckTemplateFormValues = {
  name: string;
  professional_quantity: number;
  personal_quantity: number;
  professional_questions: string[];
  personal_questions: string[];
  custom_instructions?: string;
};

const requiredQuestionTextSchema = () =>
  string()
    .transform((value) => (value == null ? "" : String(value)))
    .trim()
    .min(1, t("Enter the question text, or delete this question."));

const optionalQuestionTextSchema = () =>
  string()
    .transform((value) => (value == null ? "" : String(value)))
    .trim();

const questionsArray = () => array().of(requiredQuestionTextSchema());

const optionalQuestionsArray = () => array().of(optionalQuestionTextSchema());

function baseQuantitySchema(): NumberSchema {
  return number()
    .integer()
    .min(0, t("Quantity cannot be less than 0."))
    .max(
      MAX_REFERENCE_QUANTITY,
      t("Quantity cannot be greater than {{max}}.", {
        max: MAX_REFERENCE_QUANTITY,
      })
    )
    .required();
}

function quantityRequiresOtherOrSelf(self: "professional" | "personal") {
  const other =
    self === "professional" ? "personal_quantity" : "professional_quantity";

  return baseQuantitySchema().when(other, {
    is: (otherQty: number) => !otherQty || otherQty === 0,
    then: (schema) =>
      schema.min(
        1,
        t("At least one professional or personal reference is required.")
      ),
  });
}

export const referenceQuantityFieldsSchema = () => ({
  professional_quantity: quantityRequiresOtherOrSelf("professional"),
  personal_quantity: quantityRequiresOtherOrSelf("personal"),
});

export const REFERENCE_QUANTITY_DEPENDENCY: [string, string] = [
  "professional_quantity",
  "personal_quantity",
];

export const validationSchema = () =>
  object().shape(
    {
      name: string().trim().min(1).required().label(t("Template name")),
      ...referenceQuantityFieldsSchema(),
      professional_questions: questionsArray().when("professional_quantity", {
        is: (qty: number) => (qty ?? 0) > 0,
        then: (schema) =>
          schema.min(1, t("At least one professional question is required.")),
        otherwise: () => optionalQuestionsArray(),
      }),
      personal_questions: questionsArray().when("personal_quantity", {
        is: (qty: number) => (qty ?? 0) > 0,
        then: (schema) =>
          schema.min(1, t("At least one personal question is required.")),
        otherwise: () => optionalQuestionsArray(),
      }),
    },
    [REFERENCE_QUANTITY_DEPENDENCY]
  );

export const nameOnlyValidationSchema = () =>
  object().shape({
    name: string().trim().min(1).required().label(t("Template name")),
    custom_instructions: customInstructionsSchemaField(),
  });

function collectStringMessages(value: unknown, out: string[]): void {
  if (value == null) {
    return;
  }
  if (typeof value === "string") {
    if (value.trim()) {
      out.push(value);
    }
    return;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    out.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      collectStringMessages(item, out);
    }
    return;
  }
  if (typeof value === "object") {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      collectStringMessages(nested, out);
    }
  }
}

export function apiFailureMessage(error: unknown): string | undefined {
  if (error instanceof APIError) {
    const parsed = defaultParseError(error);
    if (parsed) {
      return parsed;
    }
    const nested: string[] = [];
    collectStringMessages(error.body, nested);
    if (nested.length > 0) {
      return [...new Set(nested)].join(", ");
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return undefined;
}
